import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DigisubservicesService } from '../services/digisubservices.service';

@Component({
  selector: 'app-issue-wise-report-advt-business',
  templateUrl: './issue-wise-report-advt-business.component.html',
  styleUrl: './issue-wise-report-advt-business.component.scss'
})
export class IssueWiseReportAdvtBusinessComponent implements OnInit {

  allCategories: any[] = [];

  isLoading = false;
  financialYearLabel = '';
  todayDate: string = '';

  // Footer balance variables
  februaryValue2025: number = 0;
  marchValue2025: number = 0;
  februaryValue2026: number = 0;
  marchValue2026: number = 0;

  // ✅ Added: Dynamic footer year labels
  footerPrevYear: string = '';        // e.g. २०२५
  footerCurrYear: string = '';        // e.g. २०२६
  footerEndDate: string = '';         // e.g. ३१/०३/२०२६
  footerPrevYearSuffix: string = '';  // e.g. २५ (last 2 digits)
  footerCurrYearSuffix: string = '';  // e.g. २६ (last 2 digits)

  categoryOrder = ['नियिमत अंक', 'विशेषांक', 'ग्रंथ', 'प्राेग्राम'];

  constructor(
    private dialogRef: MatDialogRef<IssueWiseReportAdvtBusinessComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private service: DigisubservicesService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    const payload = {
      start_date: this.data.startDate,
      end_date: this.data.endDate,
      financial_year: this.data.financial_year,
      rep_name: this.data.repname || '',
      off_rep_name: this.data.offRepName || '',
      district: this.data.district,
      taluka: this.data.taluka
    };

    this.loadBusinessReport(payload);
  }

  loadBusinessReport(payload: any): void {
    this.isLoading = true;

    this.service.getBusinessReportIssue(payload).subscribe({
      next: (res: any) => {
        console.log('API Response:', res);

        if (res?.success && Array.isArray(res.categories)) {
          let categories = res.categories.map((cat: any) => ({
            category_name: cat.category_name,
            is_special: cat.is_special,
            monthly_data: cat.monthly_data || []
          }));

          this.allCategories = this.sortCategories(categories);
          this.financialYearLabel = res.financial_year_label || '';

          // ✅ Added: Set dynamic year labels from selected financial year
          this.setDynamicFooterYears();

          // Calculate footer balance values after data is loaded
          this.calculateFooterBalances();
        }

        console.log('All Categories (Sorted):', this.allCategories);

        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: err => {
        console.error('API Error:', err);
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  // ✅ Added: Derive dynamic Devanagari year labels from financial_year input
  setDynamicFooterYears(): void {
    let startYear = 0;
    let endYear = 0;

    // Try financial_year string first e.g. "2025-2026"
    if (this.data.financial_year) {
      const parts = this.data.financial_year.split('-');
      if (parts.length === 2) {
        startYear = parseInt(parts[0].trim());
        endYear   = parseInt(parts[1].trim());
      }
    }
    // Fallback to start_date / end_date
    else if (this.data.startDate && this.data.endDate) {
      startYear = new Date(this.data.startDate).getFullYear();
      endYear   = new Date(this.data.endDate).getFullYear();
    }

    // Default fallback
    if (!startYear || !endYear) {
      const now = new Date();
      startYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
      endYear   = startYear + 1;
    }

    this.footerPrevYear       = this.convertToDevanagari(startYear.toString());
    this.footerCurrYear       = this.convertToDevanagari(endYear.toString());
    this.footerPrevYearSuffix = this.convertToDevanagari(startYear.toString().slice(-2));
    this.footerCurrYearSuffix = this.convertToDevanagari(endYear.toString().slice(-2));
    this.footerEndDate        = '३१/०३/' + this.footerCurrYear;
  }

  // ✅ Fixed: Uses dynamic suffix instead of hardcoded २५/२६
  calculateFooterBalances(): void {
    this.februaryValue2025 = 0;
    this.marchValue2025    = 0;
    this.februaryValue2026 = 0;
    this.marchValue2026    = 0;

    this.allCategories.forEach(cat => {
      cat.monthly_data.forEach((row: any) => {
        const month: string = row.month || '';

        // February row (curr year) → balance = curr year value, prev_year = prev year value
        if (month.startsWith('फेब्रुवारी') && month.endsWith(this.footerCurrYearSuffix)) {
          this.februaryValue2026 += Number(row.balance)   || 0;
          this.februaryValue2025 += Number(row.prev_year) || 0;
        }

        // March row (curr year) → balance = curr year value, prev_year = prev year value
        if (month.startsWith('मार्च') && month.endsWith(this.footerCurrYearSuffix)) {
          this.marchValue2026 += Number(row.balance)   || 0;
          this.marchValue2025 += Number(row.prev_year) || 0;
        }
      });
    });
  }

  // ✅ Added: Devanagari conversion utility in component
  convertToDevanagari(num: string): string {
    const dev: { [key: string]: string } = {
      '0':'०','1':'१','2':'२','3':'३','4':'४',
      '5':'५','6':'६','7':'७','8':'८','9':'९'
    };
    return num.replace(/[0-9]/g, d => dev[d]);
  }

  sortCategories(categories: any[]): any[] {
    return categories.sort((a, b) => {
      let indexA = this.categoryOrder.indexOf(a.category_name);
      let indexB = this.categoryOrder.indexOf(b.category_name);
      if (indexA === -1) indexA = 999;
      if (indexB === -1) indexB = 999;
      return indexA - indexB;
    });
  }

  calculateCategoryTotal(categoryData: any[], field: string): number {
    if (!categoryData || categoryData.length === 0) return 0;
    return categoryData.reduce((sum, item) => sum + (Number(item[field]) || 0), 0);
  }

  calculateTotal(data: any[], field: string): number {
    if (!data || data.length === 0) return 0;
    return data.reduce((sum, item) => sum + (Number(item[field]) || 0), 0);
  }

  calculateGrandTotal(field: string): number {
    let total = 0;
    this.allCategories.forEach(cat => {
      total += this.calculateTotal(cat.monthly_data, field);
    });
    return total;
  }

  formatCurrency(value: number): string {
    return value ? value.toString() : '0';
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  exportToExcel(): void {
    const table = document.getElementById('business-report-table');
    if (!table) return;

    import('xlsx').then(xlsx => {
      const ws = xlsx.utils.table_to_sheet(table);
      const wb = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(wb, ws, 'व्यवसाय अहवाल');
      xlsx.writeFile(wb, 'Issue_Wise_Business_Report.xlsx');
    });
  }

  printPageA4Portrait(): void {
    const printSection = document.getElementById('print-area');
    if (!printSection) return;

    const win = window.open('', '', 'width=800,height=1100');
    if (!win) return;

    win.document.write(`
      <html>
      <head>
        <style>
          @page { size: A4 portrait; margin: 8mm; }
          @media print {
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
          }
          body { font-family: Arial; margin: 0; padding: 0; }
          table { border-collapse: collapse; width: 100%; }
          th {
            border: 1px solid #000;
            padding: 4px 6px;
            font-size: 12px;
            text-align: center;
            white-space: nowrap;
          }
          td {
            border: 1px solid #000;
            padding: 4px 6px;
            font-size: 12px;
            text-align: end;
            white-space: nowrap;
          }
          .text-left { text-align: left !important; padding-left: 8px; }
          .text-right { text-align: right !important; padding-right: 8px; }
          .bg-light { background-color: #f0f0f0; }
          .total-row { font-weight: bold; background-color: #e0e0e0; text-align: end !important; }
          .category-total-row { font-weight: bold; background-color: #ffd700; text-align: end !important; }
          .grand-total-row { font-weight: bold; background-color: #00ffff; text-align: end !important; }
          .section-divider {
            font-weight: bold;
            text-align: right;
            background-color: #5f9ea0;
            padding: 5px;
            color: #000;
          }
          .special-header-row { background-color: #f5f5f5; }
          thead { display: table-header-group; }
          tfoot { display: table-footer-group; }
        </style>
      </head>
      <body>
        ${printSection.outerHTML}
        <script>
          window.onload = () => {
            setTimeout(() => {
              window.print();
              window.close();
            }, 300);
          };
        </script>
      </body>
      </html>
    `);

    win.document.close();
  }
}